<template>
  <div>
    <div class="relative h-[50vh] w-screen overflow-hidden md:h-[80vh]">
      <!-- Text Protect -->
      <div class="absolute top-0 z-10 h-1/3 w-screen bg-gradient-to-b from-charcoal-100 mix-blend-multiply" />
      <PrismicImage
        v-if="page"
        class="h-[50vh] w-screen object-cover md:h-[80vh]"
        :field="page?.data.featured_image"
      />
    </div>

    <!-- Details -->
    <div class="container grid grid-cols-1 gap-20 py-10 xl:grid-cols-content xl:py-20">
      <!-- Intro -->
      <div class="content-start">
        <h1 class="my-10 text-header2 md:text-desktop-header2">
          {{ page?.data.title }}
        </h1>
        <PrismicRichText
          :field="page?.data.description"
          class="text-large-copy md:text-desktop-large-copy"
        />

        <!-- Slices -->
        <SliceZone
          wrapper="main"
          :slices="page?.data.slices ?? []"
          :components="components"
        />
      </div>

      <!-- Event Bar -->
      <div class="h-100 sticky top-40 mt-4">
        <div class="content-start">
          <!-- Book Now Button - Hidden if no Script or Form ID -->
          <UIButton
            v-if="page?.data.registration_form_id"
            type="primary"
            label="Book Now"
            :block="true"
            class="mb-10"
            @click="toggleModal"
          />

          <!-- Date -->
          <div class="my-4">
            <h4 class="text-large-copy">
              Date
            </h4>
            <span v-if="dayjs(page?.data.start_date).isSame(page?.data.end_date, 'day')">
              {{ dayjs(page?.data.start_date).format("DD MMMM YYYY, hh:mm A") }}
            </span>
            <span v-else>
              {{ dayjs(page?.data.start_date).format("DD MMMM") }}
              -
              {{ dayjs(page?.data.end_date).format("DD MMMM YYYY") }}
            </span>
          </div>

          <!-- Day Pass -->
          <div
            v-if="page?.data.day_pass"
            class="my-4 border-y py-4"
          >
            <h4 class="text-body-copy">
              ✓ Included with Day Pass
              <!-- <br />
              ✓ Free for Members -->
            </h4>
          </div>

          <!-- Admission Fees -->
          <div class="my-4">
            <h4 class="text-large-copy">
              Admission Fees
            </h4>
            <div v-if="page?.data.admission_fees.length">
              <p
                v-for="fee in page?.data.admission_fees"
                :key="fee.audience"
              >
                {{ fee.price ? useCurrencyFormat(fee.price) : 'Free for' }} {{ fee.audience }}
              </p>
            </div>
            <div v-else>
              <p>Free Entry</p>
            </div>
          </div>

          <!-- Plan your visit -->
          <UIButton
            :type="'text'"
            :label="'Plan your Visit'"
            class="my-4 block"
            :link="prismic.asLink(settings?.data.gm_link)"
            target="blank"
          />

          <!-- Promo Banners -->
          <div v-if="page?.data.promo_banners && page?.data.promo_banners[0]?.title">
            <div
              v-for="banner in page?.data.promo_banners"
              :key="banner.title"
              class="my-4 block"
            >
              <NuxtLink :to="prismic.asLink(banner.link) ?? ''">
                <div
                  class="group w-full cursor-pointer p-4 text-charcoal-100"
                  :class="useTheme(banner.theme)"
                >
                  <div class="flex flex-row items-end justify-between">
                    <h2 class="text-large-copy !leading-9 md:text-desktop-large-copy">
                      {{ banner.title }}
                    </h2>
                    <ArrowRight class="w-6 group-hover:animate-bouncex md:w-10" />
                  </div>
                </div>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Event Booking Modal - Hidden if no Script or Form ID -->
    <EventsForm
      v-if="page?.data.registration_form_id"
      v-show="modalOpen"
      :toggle="toggleModal"
      :form="page?.data.registration_form_id"
    />
  </div>
</template>

<script setup lang="ts">
import dayjs from "dayjs";
import { components } from "~/slices";
import ArrowRight from "~/assets/svgs/arrow_right.svg";

const prismic = usePrismic();
const route = useRoute();
const config = useRuntimeConfig();

const { useCurrencyFormat } = useFilters();

const modalOpen = ref(false);

const { data: page } = await useAsyncData(route.params.uid as string, async () =>
  prismic.client.getByUID("events", route.params.uid as string),
);
if (!page.value) {
  throw createError({ statusCode: 404 });
}

const { data: settings } = await useAsyncData("settings", async () =>
  prismic.client.getSingle("settings"),
);

const toggleModal = () => {
  modalOpen.value = !modalOpen.value;
};

useSeoMeta({
  description: () => page.value?.data.meta_description?.length ? page.value.data.meta_description : prismic.asText(page.value?.data.description),
  ogTitle: () => page.value?.data.meta_title?.length ? page.value.data.meta_title : page.value?.data.title,
  ogUrl: () => `${config.public.BASE_URL}${route.fullPath}`,
  ogDescription: () => page.value?.data.meta_description?.length ? page.value.data.meta_description : prismic.asText(page.value?.data.description),
  ogImage: () => page.value?.data.meta_image.url?.length ? page.value.data.meta_image.url : `${config.public.BASE_URL}/lightbox_gallery.jpg`,
  ogImageType: () => "image/jpeg",
  ogImageWidth: () => "1200",
  ogImageHeight: () => "630",
  ogType: () => "website",
  twitterTitle: () => page.value?.data.meta_title?.length ? page.value.data.meta_title : page.value?.data.title,
  twitterImage: () => page.value?.data.meta_image.url?.length ? page.value.data.meta_image.url : `${config.public.BASE_URL}/lightbox_gallery.jpg`,
  twitterDescription: () => page.value?.data.meta_description?.length ? page.value.data.meta_description : prismic.asText(page.value?.data.description),
});
</script>
